import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { login, loginEx } from '@/hooks/useAuth';
import { useLanguage } from '@/hooks/useLanguage';

const CHIPS = [
  { icon: '⚡', label: 'Forecast', color: '#B8860B', bg: 'rgba(184,134,11,0.12)', border: 'rgba(184,134,11,0.25)' },
  { icon: '🟢', label: 'BESS', color: '#0D7A3C', bg: 'rgba(13,122,60,0.1)', border: 'rgba(13,122,60,0.22)' },
  { icon: '📊', label: 'Trade', color: '#1F5B8F', bg: 'rgba(31,91,143,0.1)', border: 'rgba(31,91,143,0.22)' },
  { icon: '🔴', label: 'GO', color: '#D12229', bg: 'rgba(209,34,41,0.12)', border: 'rgba(209,34,41,0.25)' },
  { icon: '⚙', label: 'Ops', badge: 'NEW', color: '#A0A0A8', bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.12)' },
  { icon: null, label: '+ 18 модулів', color: '#6B6B74', bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.09)' },
];

const FLAGS: Record<string, string> = { UA: '🇺🇦', PL: '🇵🇱', EN: '🇬🇧' };

export default function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, setLanguage } = useLanguage();

  const [loginInput, setLoginInput] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError('');
    if (!loginInput.trim() || !password) {
      setError(t('login_error_empty'));
      return;
    }
    setLoading(true);
    try {
      const r = await loginEx(loginInput, password);
      if ('user' in r) {
        const user = r.user;
        // Owner (CEO) lands on the consolidated owner dashboard, not the admin home.
        navigate(user.role === 'CEO' ? '/dashboard/owner' : user.role === 'Оператор' ? '/dashboard/dispatcher' : '/');
      } else if (r.error.kind === 'rate_limited') {
        // Ліміт спроб — пароль тут ні до чого, і підказка має це казати.
        setError(t('login_error_rate', { sec: r.error.retryAfterSec }));
      } else if (r.error.kind === 'unavailable') {
        setError(t('login_error_unavailable'));
      } else {
        setError(t('login_error_wrong'));
        setPassword('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit();
  };

  const inputStyle = (field: string) => ({
    background: focusedField === field ? '#fff' : '#FAFAFA',
    border: `1px solid ${focusedField === field ? '#D12229' : error ? '#F0B0B3' : '#E5E5E8'}`,
    color: '#1A1A1A',
    fontFamily: 'Inter, sans-serif',
    outline: 'none',
    transition: 'all 0.15s',
  });

  return (
    <div className="min-h-screen flex overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* ─── LEFT DARK HERO ─── */}
      <div
        className="hidden lg:flex flex-col w-[62%] relative overflow-hidden"
        style={{ background: '#0E0E10' }}
      >
        {/* Red glow top-left */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: '-80px', left: '-60px',
            width: '540px', height: '440px',
            background: 'radial-gradient(ellipse, rgba(209,34,41,0.30) 0%, rgba(209,34,41,0.07) 45%, transparent 70%)',
            filter: 'blur(24px)',
          }}
        />
        {/* Subtle bottom-right accent */}
        <div
          className="absolute pointer-events-none"
          style={{
            bottom: '0', right: '0',
            width: '280px', height: '200px',
            background: 'radial-gradient(ellipse, rgba(209,34,41,0.07) 0%, transparent 70%)',
          }}
        />

        {/* Content */}
        <div className="flex flex-col justify-center flex-1 px-14 py-16 relative z-10">
          {/* Brand name */}
          <p
            className="font-semibold mb-3 tracking-widest uppercase"
            style={{ color: '#D12229', fontSize: '13px', letterSpacing: '0.18em' }}
          >
            Helios Aggregate
          </p>
          {/* Headline */}
          <h1
            className="font-bold leading-[1.1] mb-5"
            style={{ color: '#FFFFFF', fontSize: '46px', letterSpacing: '-0.03em', maxWidth: '480px' }}
          >
            Єдина платформа<br />
            для вашої{' '}
            <span style={{ color: '#D12229' }}>СЕС</span>
          </h1>

          {/* Description */}
          <p className="text-sm leading-relaxed mb-10" style={{ color: '#72727A', maxWidth: '430px', lineHeight: '1.75' }}>
            Прогнозування · диспетчеризація · АСКОЕ · балансування ·{' '}
            GO-сертифікати · технічна експлуатація — усе в одному
            кабінеті, 23 модулі на вибір.
          </p>

          {/* Module chips */}
          <div className="flex flex-wrap gap-2">
            {CHIPS.map((chip) => (
              <span
                key={chip.label}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                style={{ background: chip.bg, color: chip.color, border: `1px solid ${chip.border}` }}
              >
                {chip.icon && <span className="text-[11px]">{chip.icon}</span>}
                {chip.label}
                {chip.badge && (
                  <span className="ml-0.5 px-1.5 rounded-full text-[9px] font-bold" style={{ background: '#D12229', color: '#fff' }}>
                    {chip.badge}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom accent line */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{ background: 'linear-gradient(90deg, rgba(209,34,41,0.45) 0%, transparent 60%)' }}
        />
      </div>

      {/* ─── RIGHT FORM ─── */}
      <div className="flex-1 flex flex-col relative overflow-hidden" style={{ background: '#FFFFFF' }}>
        {/* Bottom-right glow */}
        <div
          className="absolute pointer-events-none"
          style={{
            bottom: '-40px', right: '-40px',
            width: '380px', height: '300px',
            background: 'radial-gradient(ellipse, rgba(209,34,41,0.09) 0%, rgba(209,34,41,0.03) 50%, transparent 70%)',
          }}
        />

        <div className="flex flex-col justify-center flex-1 px-12 py-12 relative z-10 max-w-[440px] mx-auto w-full">

          {/* Heading */}
          <div className="mb-8 flex flex-col items-center text-center">
            <img
              src="https://static.readdy.ai/image/14febd11ed764a2e4131edc63c3ba26f/74560fb084c4b05e31d7a6da8de31caf.png"
              alt="Helios AG"
              className="mb-5 w-auto object-contain"
              style={{ height: '72px' }}
            />
            <h2
              className="font-bold mb-1.5"
              style={{ color: '#1A1A1A', fontSize: '26px', letterSpacing: '-0.02em' }}
            >
              {t('login_welcome')}
            </h2>
            <p className="text-sm" style={{ color: '#8B8B94' }}>
              {t('login_subtitle')}
            </p>
          </div>

          {/* Login field */}
          <div className="mb-4">
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>
              {t('login_label_login')}
            </label>
            <input
              type="text"
              value={loginInput}
              onChange={(e) => { setLoginInput(e.target.value); setError(''); }}
              onKeyDown={handleKeyDown}
              onFocus={() => setFocusedField('login')}
              onBlur={() => setFocusedField(null)}
              placeholder="username"
              className="w-full px-3.5 py-2.5 rounded-lg text-[16px] sm:text-sm"
              style={inputStyle('login')}
              autoComplete="username"
            />
          </div>

          {/* Password field */}
          <div className="mb-2">
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#1A1A1A' }}>
              {t('login_label_password')}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                onKeyDown={handleKeyDown}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                placeholder="••••••••••"
                className="w-full px-3.5 pr-10 py-2.5 rounded-lg text-[16px] sm:text-sm"
                style={inputStyle('password')}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer w-5 h-5 flex items-center justify-center"
              >
                <i
                  className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}
                  style={{ color: '#AFAFB8' }}
                />
              </button>
            </div>
          </div>

          {/* Error message */}
          <div className="mb-4 h-5">
            {error && (
              <div className="flex items-center gap-1.5">
                <i className="ri-error-warning-line text-xs" style={{ color: '#D12229' }} />
                <p className="text-xs font-medium" style={{ color: '#D12229' }}>{error}</p>
              </div>
            )}
          </div>

          {/* Primary CTA */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-3 rounded-lg text-sm font-semibold cursor-pointer transition-all whitespace-nowrap flex items-center justify-center gap-2 mb-3"
            style={{
              background: loading ? 'rgba(209,34,41,0.75)' : '#D12229',
              color: '#fff',
              fontFamily: 'Inter, sans-serif',
              letterSpacing: '0.01em',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#B81E24'; }}
            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#D12229'; }}
          >
            {loading
              ? <><i className="ri-loader-4-line animate-spin text-base" /> {t('login_checking')}</>
              : <>{t('login_btn')}</>
            }
          </button>

          {/* SSO + Magic-link */}
          <div className="grid grid-cols-2 gap-2.5 mb-6">
            {[
              { icon: '🔑', label: 'SSO Google' },
              { icon: '∞', label: 'Magic-link', mono: true },
            ].map((btn) => (
              <button
                key={btn.label}
                type="button"
                className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap"
                style={{ background: '#FAFAFA', border: '1px solid #E5E5E8', color: '#1A1A1A' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#F0F0F2'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#FAFAFA'; }}
              >
                <span
                  className={btn.mono ? 'font-bold text-sm' : 'text-sm'}
                  style={btn.mono ? { color: '#6B6B74' } : {}}
                >
                  {btn.icon}
                </span>
                {btn.label}
              </button>
            ))}
          </div>

          {/* Helper links */}
          <div className="space-y-2 text-center">
            <p className="text-xs" style={{ color: '#8B8B94' }}>
              {t('login_forgot')}{' '}
              <button type="button" className="cursor-pointer font-medium hover:opacity-75 whitespace-nowrap" style={{ color: '#D12229' }}>
                {t('login_restore')}
              </button>
            </p>
            <p className="text-xs" style={{ color: '#8B8B94' }}>
              {t('login_new_company')}{' '}
              <button type="button" className="cursor-pointer font-medium hover:opacity-75 whitespace-nowrap" style={{ color: '#D12229' }}>
                {t('login_demo')}
              </button>
            </p>
          </div>

          {/* Language switch — connected to global i18n */}
          <div className="flex items-center justify-center gap-1.5 mt-8">
            {(['UA', 'PL', 'EN'] as const).map((lang, idx) => (
              <span key={lang} className="flex items-center gap-1.5">
                {idx > 0 && <span className="text-xs" style={{ color: '#DCDCE0' }}>·</span>}
                <button
                  type="button"
                  onClick={() => setLanguage(lang)}
                  className="flex items-center gap-1 cursor-pointer transition-opacity whitespace-nowrap"
                  style={{ opacity: language === lang ? 1 : 0.4 }}
                >
                  <span className="text-sm">{FLAGS[lang]}</span>
                  <span className="text-xs font-semibold" style={{ color: language === lang ? '#1A1A1A' : '#8B8B94' }}>
                    {lang}
                  </span>
                </button>
              </span>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}
